import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {IProjekt, isDraft, isExportable, Projekt} from '../../../../shared/model/projekt.model';
import {DatePipe, NgTemplateOutlet} from '@angular/common';
import {SkillSelectComponent} from '../skill-select/skill-select.component';
import {IFaehigkeit} from '../../../../shared/model/faehigkeit.model';
import {HttpClient} from '@angular/common/http';
import {Aufgabe, AufgabeValidation, ValidationMessage} from '../../../../shared/model/aufgabe.model';
import {FormsModule, NgForm} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {
  MatDatepicker,
  MatDatepickerActions,
  MatDatepickerApply,
  MatDatepickerCancel,
  MatDatepickerInput,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import {MatError, MatFormField, MatHint, MatInput, MatLabel, MatSuffix} from '@angular/material/input';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {MatIcon} from '@angular/material/icon';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MatDivider} from '@angular/material/divider';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ModalService} from '../../../../shared/components/modal/modal.service';
import {MessageService} from '../../../../shared/components/message.service';
import {
  IndustriesDropdownComponent
} from '../../personal-data/dropdowns/industries-dropdown/industries-dropdown.component';
import {RequestsService} from '../../../../services/requests.service';
import {Requests, RequestType} from '../../../../shared/model/request.model';
import {map} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {VocabularyService} from '../../../../services/vocabulary.service';
import {VocabularyPipe} from '../../../../shared/vocabulary.pipe';
import {Observable, shareReplay} from 'rxjs';
import {FaehigkeitenService} from '../../../../services/faehigkeiten.service';
import {INPUT_LIMITS} from '../../../../shared/constants/input-limits';
import {Badge} from '@spirit-testing/component-library';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatTooltip} from '@angular/material/tooltip';
import {CiMultiselectComponent} from '../../../../../profiler-theme';
import {TrimmedLengthDirective} from '../../../../shared/trimmed-length.directive';
import {normalizeWhitespace, normalizedLength} from '../../../../shared/text-length';
import {listProjectFindings} from '../../../../shared/model/projekt-validation';
import {ValidationFinding} from '../../../../shared/model/validation-finding';
import {FieldHintComponent} from '../../../../shared/components/field-hint/field-hint.component';
import {rebaseProjectEdits} from './project-edit-rebase';

@Component({
  selector: 'app-project',
  imports: [
    DatePipe,
    SkillSelectComponent,
    FormsModule,
    MatButton,
    MatDatepicker,
    MatFormField,
    MatInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepickerInput,
    MatFormField,
    MatLabel,
    MatDatepickerCancel,
    MatDatepickerApply,
    MatDatepickerActions,
    TranslatePipe,
    MatIcon,
    MatCard,
    MatCardContent,
    MatDivider,
    MatHint,
    CdkTextareaAutosize,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatAccordion,
    ModalComponent,
    IndustriesDropdownComponent,
    MatError,
    Badge,
    MatCheckbox,
    MatTooltip,
    MatProgressSpinner,
    CiMultiselectComponent,
    TrimmedLengthDirective,
    NgTemplateOutlet,
    FieldHintComponent,
    VocabularyPipe,
  ],
  templateUrl: './project.component.html',
  styleUrls: ['./project.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectComponent implements OnInit, OnChanges, DoCheck {
  @ViewChildren('textAreaTask') textElementTask: QueryList<ElementRef>;
  @ViewChild('projectForm') projectForm?: NgForm;

  @Input() projekt: IProjekt;
  @Input() isNewProject: boolean = false;
  @Input() exportMode: boolean;
  @Input() twoPagerMode: boolean = false;
  @Input() remainingMethodSlots: number = 3;
  @Input() initialTaskIds: string[] | null = null;
  @Input() initialMethodIds: string[] | null = null;
  // Two-Pager: gemeinsame Skill-Auswahl (kontrolliert vom Parent, synchron mit der Fähigkeiten-Kachel).
  @Input() selectedSkillIds: string[] = [];
  @Input() skillBudgetReached: boolean = false;
  @Input() isSaving = false;
  @Input() failedSave: IProjekt | null = null;
  /** NP20-1057: Zwischenstände gibt es nur für den Profilbesitzer; Dritte geben wie heute direkt frei. */
  @Input() canSaveDraft = false;
  @Input() resumeEdit = false;

  @Output() deletedProject = new EventEmitter<void>();
  @Output() changedProject = new EventEmitter<IProjekt>();
  @Output() draftProject = new EventEmitter<IProjekt>();
  @Output() discardDraft = new EventEmitter<void>();
  @Output() cancelEdit = new EventEmitter<void>();
  @Output() exportSelectionChange = new EventEmitter<string[]>();
  @Output() methodSelectionChange = new EventEmitter<string[]>();
  @Output() skillSelectionChange = new EventEmitter<string[]>();
  @Output() taskSave = new EventEmitter<Aufgabe>();

  selectedExportIds: string[] = [];
  selectedMethodIds: string[] = [];

  protected get LIMITS() { return INPUT_LIMITS; }

  skillList: IFaehigkeit[] = [];
  methodNames: string[] = [];
  private methodSource: string[] = [];
  validationResults: AufgabeValidation[] = [];
  newTask: Aufgabe;
  projectLocal: IProjekt;
  // Snapshot des Originalprojekts, damit hasChanges() nicht in jedem CD-Zyklus neu serialisiert.
  private originalProjektJson = '';
  private hasUnsavedChanges = false;
  activeTask: number;
  showModal = false;
  currentIdx: number | null = null;
  availableIndustries$!: Observable<Requests[]>;
  cachedIndustries: Requests[] = [];
  selectedIndustryNames: string[] = [];
  private savedFindings: ValidationFinding[] = [];
  private pendingTask: Aufgabe | null = null;
  private pendingSent: Aufgabe | null = null;
  private sentNewTasks: Aufgabe[] = [];
  unsavedTasks: boolean[] = [];
  unsavedHead = false;

  constructor(
    protected http: HttpClient,
    private modalService: ModalService,
    private skillService: FaehigkeitenService,
    private requestsService: RequestsService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private vocabulary: VocabularyService,
    private host: ElementRef<HTMLElement>,
  ) {
    this.vocabulary.changes$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.methodLabel = name => this.vocabulary.methodName(name);
      this.sortMethods();
      this.cdr.markForCheck();
    });
  }

  /** Neu zugewiesen bei jedem Sprachwechsel, damit die OnPush-Auswahlliste neu zeichnet. */
  methodLabel = (name: string): string => this.vocabulary.methodName(name);

  private sortMethods() {
    this.methodNames = this.vocabulary.sort(this.methodSource, this.methodLabel);
  }

  private _editMode: boolean = false;

  get editMode(): boolean {
    return this._editMode;
  }

  set editMode(value: boolean) {
    this._editMode = value;
    if (value === true) {
      Promise.resolve().then(() => setTimeout(() => {
        this.runAllTaskValidations();
        this.projectForm?.control.markAllAsTouched();
        this.cdr.markForCheck();
      }));
    }
  }

  ngOnInit() {
    this.loadSkills();
    this.projectLocal = Projekt.clone(this.projekt);
    this.captureOriginalSnapshot();
    this.refreshFindings();
    this.getAllIndustries();
    this.availableIndustries$.subscribe(industries => {
      this.cachedIndustries = industries;
      this.updateSelectedIndustryNames();
      this.cdr.markForCheck();
    });
    if (this.resumeEdit) {
      this.onToggleEdit();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const wasEditing = this._editMode;
    const localBefore = this.projectLocal;
    const pendingIndex = this.pendingTask ? this.projectLocal?.tasks?.indexOf(this.pendingTask) ?? -1 : -1;
    const sentNewIndexes = this.sentNewTasks.map(t => this.projectLocal?.tasks?.indexOf(t) ?? -1);
    if (changes['projekt'] && this.projekt) {
      if (!this.availableIndustries$) {
        this.getAllIndustries();
      }

      this.projectLocal = this.keepsEdits(changes)
        ? Projekt.clone(rebaseProjectEdits(
          Projekt.clone(JSON.parse(this.originalProjektJson)),
          Projekt.clone(this.projectLocal),
          Projekt.clone(this.projekt),
          pendingIndex >= 0 && this.pendingSent ? {index: pendingIndex, sent: this.pendingSent} : null,
          sentNewIndexes
        ))
        : Projekt.clone(this.projekt);
      this.sentNewTasks = this.followSentNewTasks(localBefore);
      this.captureOriginalSnapshot();
      this.refreshFindings();
      this.initProjektData();

      this.selectedMethodIds = [];
      // Only sync export-selection state up to the parent when we are actually in an
      // export / two-pager flow. Emitting during normal editing fed straight back into
      // the parent's [initialMethodIds]/[initialTaskIds] bindings within the same change
      // detection pass (null -> []), causing NG0100 and a CD storm (NP20-745).
      const exportContext = this.exportMode || this.twoPagerMode;
      if (exportContext) {
        this.methodSelectionChange.emit([]);
      }

      const hasInitialTasks = changes['initialTaskIds'] && this.initialTaskIds !== null;
      if (!hasInitialTasks) {
        if (this.exportMode && !this.twoPagerMode) {
          this.autoSelectAllTasks();
        } else {
          this.selectedExportIds = [];
          if (exportContext) {
            this.exportSelectionChange.emit([]);
          }
        }
      }

      if (this.isNewProject) {
        this.editMode = true;
      } else if (!changes['projekt'].firstChange && this._editMode) {
        // NP20-893: Projektwechsel im aktiven Bearbeitungs-Modus. `projectLocal`
        // haengt jetzt am neuen Projekt, aber die Editmode-Side-Effects
        // (markAllAsTouched + runAllTaskValidations) laufen sonst nicht neu —
        // die Pflichtfeld-Fehler des neuen Projekts blieben unsichtbar, obwohl
        // sie fachlich bereits vorliegen.
        this.editMode = true;
      }
    }

    const saving = changes['isSaving'];
    if (saving && saving.previousValue && !saving.currentValue) this.sentNewTasks = [];
    if (saving && saving.previousValue && !saving.currentValue && this.pendingTask) {
      if (pendingIndex >= 0) this.restoreTaskSaveFocus(pendingIndex);
      this.pendingTask = null;
      this.pendingSent = null;
    }

    if (changes['failedSave'] && this.failedSave) {
      this.projectLocal = wasEditing && localBefore ? localBefore : Projekt.clone(this.failedSave);
      this.initProjektData();
      this.editMode = true;
    }

    if (changes['initialTaskIds'] && this.initialTaskIds !== null) {
      this.selectedExportIds = [...this.initialTaskIds];
      this.exportSelectionChange.emit([...this.selectedExportIds]);
    }

    if (changes['initialMethodIds'] && this.initialMethodIds !== null) {
      this.selectedMethodIds = [...this.initialMethodIds];
      this.methodSelectionChange.emit([...this.selectedMethodIds]);
    }

    if (changes['exportMode'] || changes['twoPagerMode']) {
      if (this.exportMode && !this.twoPagerMode) {
        this.autoSelectAllTasks();
      } else if (!this.exportMode) {
        this.selectedExportIds = [];
        this.selectedMethodIds = [];
      } else if (this.twoPagerMode) {
        this.selectedMethodIds = [];
      }
    }
  }

  private followSentNewTasks(localBefore: IProjekt | undefined): Aufgabe[] {
    const before = (localBefore?.tasks ?? []).filter(t => !t.uuid);
    const after = (this.projectLocal?.tasks ?? []).filter(t => !t.uuid);
    if (before.length !== after.length) return [];
    return this.sentNewTasks.map(t => after[before.indexOf(t)]).filter(t => !!t);
  }

  private keepsEdits(changes: SimpleChanges): boolean {
    return !changes['projekt'].firstChange && this._editMode && !this.isNewProject
      && !!this.projectLocal && this.projectLocal.uuid === this.projekt.uuid;
  }

  private restoreTaskSaveFocus(idx: number): void {
    setTimeout(() => {
      const active = document.activeElement;
      if (active && active !== document.body) return;
      const buttons = this.host.nativeElement.querySelectorAll<HTMLButtonElement>('.project__task-save-button');
      buttons[idx]?.focus();
    });
  }

  private autoSelectAllTasks(): void {
    this.selectedExportIds = !this.exportable ? [] :
      (this.projekt?.tasks ?? []).map(t => t.uuid).filter((uuid): uuid is string => !!uuid);
    this.exportSelectionChange.emit([...this.selectedExportIds]);
  }

  toggleExportSelection(idx: string, checked: boolean): void {
    if (checked) {
      if (!this.selectedExportIds.includes(idx)) this.selectedExportIds.push(idx);
    } else {
      this.selectedExportIds = this.selectedExportIds.filter(i => i !== idx);
    }
    this.exportSelectionChange.emit([...this.selectedExportIds]);
  }

  // Aufgaben sind im Two-Pager nicht mehr begrenzt (Seitenanzahl steuert das Layout).
  canSelectTask(): boolean {
    return true;
  }

  // Task-Skill in die gemeinsame Auswahl übernehmen/entfernen (Parent hält die Gesamtmenge).
  toggleTaskSkill(id: string, checked: boolean): void {
    const current = this.selectedSkillIds ?? [];
    const next = checked ? [...current, id] : current.filter(i => i !== id);
    this.skillSelectionChange.emit(next);
  }

  canSelectTaskSkill(skill: IFaehigkeit): boolean {
    return (this.selectedSkillIds ?? []).includes(skill.id) || !this.skillBudgetReached;
  }

  toggleMethodExport(method: string, checked: boolean): void {
    if (checked) {
      if (!this.canSelectMethod(method)) return;
      if (!this.selectedMethodIds.includes(method)) {
        this.selectedMethodIds.push(method);
      }
    } else {
      this.selectedMethodIds = this.selectedMethodIds.filter(m => m !== method);
    }
    this.methodSelectionChange.emit([...this.selectedMethodIds]);
  }

  canSelectMethod(method: string): boolean {
    if (this.selectedMethodIds.includes(method)) return true;
    return this.selectedMethodIds.length < this.remainingMethodSlots;
  }

  newBeraterSkills(event: string[] | IFaehigkeit[], idx: number) {
    this.projectLocal.tasks[idx].skills = [...(event as IFaehigkeit[])];
  }

  newBeraterMethod(event: IFaehigkeit[] | string[]) {
    this.projectLocal.methodik = [...(event as string[])];
  }

  createNewTask() {
    this.newTask = new Aufgabe('', '', []);
    this.projectLocal.tasks.push(this.newTask);
    this.validationResults = [...this.validationResults, this.newTask.validate()];
    setTimeout(() => {
      this.toggleActiveTask(this.projectLocal.tasks.length - 1);
      this.cdr.markForCheck();
    });
  }

  onTaskInputChange(index: number) {
    const task = new Aufgabe(
      this.projectLocal.tasks[index].caption,
      this.projectLocal.tasks[index].details,
      this.projectLocal.tasks[index].skills
    );
    this.validationResults = [
      ...this.validationResults.slice(0, index),
      task.validate(),
      ...this.validationResults.slice(index + 1),
    ];
    this.cdr.markForCheck();
  }

  onTaskCaptionBlur(index: number): void {
    this.normalizeTaskField(index, 'caption');
  }

  onTaskDetailsBlur(index: number): void {
    this.normalizeTaskField(index, 'details');
  }

  onDescriptionBlur(): void {
    const value = this.projectLocal?.beschreibung;
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (trimmed === value) return;
    this.projectLocal.beschreibung = trimmed;
    this.cdr.markForCheck();
  }

  /** Laenge, gegen die validiert wird — Rand-Leerzeichen zaehlen nicht mit. */
  trimmedLength(value: string | null | undefined): number {
    return (value ?? '').trim().length;
  }

  /** Laenge des Aufgabentitels — zusaetzlich zaehlt jede Folge von Leerraum als ein Zeichen. */
  captionLength(value: string | null | undefined): number {
    return normalizedLength(value);
  }

  private normalizeTaskField(index: number, field: 'caption' | 'details'): void {
    const task = this.projectLocal?.tasks?.[index];
    if (!task) return;
    const value = task[field];
    if (typeof value === 'string') {
      const normalized = field === 'caption' ? normalizeWhitespace(value) : value.trim();
      if (normalized !== value) {
        task[field] = normalized;
      }
    }
    this.onTaskInputChange(index);
  }

  // NP20-828: True wenn der Task echte Fehler (severity=error) hat — nur diese
  // triggern den roten Rand am Panel. Warn/Hinweise bleiben inline sichtbar,
  // aber sollen visuell nicht als "Fehler" aussehen.
  hasTaskErrors(validation: AufgabeValidation): boolean {
    return this.getBlockingMessages(validation).length > 0;
  }

  /** Blockierende Meldungen — das ist die Menge, die den Speichern-Button lahmlegt. */
  getBlockingMessages(validation: AufgabeValidation): ValidationMessage[] {
    return this.allMessages(validation).filter(msg => msg.severity === 'error');
  }

  /**
   * Reine Hinweise, also alles Nicht-Blockierende (heute die Skills-Meldungen:
   * skills.missing/tooFew/tooMany/explainUsage). Sie haben kein Form-Control und
   * koennen deshalb nicht per mat-error angezeigt werden, dafuer gibt es die
   * Hint-Liste unter dem Details-Feld.
   *
   * Bewusst disjunkt zu getBlockingMessages(): vorher zaehlte das "N Hinweise"-Badge
   * die Fehler mit, wodurch dieselbe Meldung zweimal im Header stand und die Summe
   * nicht zum Fehler-Badge auf der Projektkarte passte.
   */
  getHintMessages(validation: AufgabeValidation): ValidationMessage[] {
    return this.allMessages(validation).filter(msg => msg.severity !== 'error');
  }

  getSkillErrors(validation: AufgabeValidation): ValidationMessage[] {
    return validation.skills.filter(msg => msg.severity === 'error');
  }

  private allMessages(validation: AufgabeValidation): ValidationMessage[] {
    return [
      ...validation.caption,
      ...validation.details,
      ...validation.skills,
    ];
  }

  runAllTaskValidations(): void {
    const tasks = this.projectLocal?.tasks ?? [];
    for (let idx = 0; idx < tasks.length; idx++) {
      this.onTaskInputChange(idx);
    }
  }

  private refreshFindings(): void {
    this.savedFindings = listProjectFindings(this.projekt);
  }

  private get showsFindings(): boolean {
    return !this.editMode && !this.exportMode;
  }

  fieldFinding(field: string): ValidationFinding | null {
    if (!this.showsFindings) return null;
    return this.savedFindings.find(
      finding => finding.section === 'projects' && finding.field === field
    ) ?? null;
  }

  taskFinding(index: number, field: string): ValidationFinding | null {
    if (!this.showsFindings) return null;
    return this.savedFindings.find(
      finding => finding.section === 'tasks' && finding.field === field && finding.task?.index === index
    ) ?? null;
  }

  get tasksSectionFinding(): ValidationFinding | null {
    if (!this.showsFindings) return null;
    return this.savedFindings.find(
      finding => finding.section === 'tasks' && !finding.task
    ) ?? null;
  }

  save(form: NgForm) {
    // NP20-892: Der Guard muss symmetrisch zum `[disabled]` des Buttons sein —
    // `(ngSubmit)` feuert auch bei Enter in einem Feld, der Button-Disabled-State
    // greift dort nicht. `tasks` sind keine Form-Controls, deshalb bleibt
    // `form.invalid` `false`, auch wenn die Aufgabenliste leer ist. Ohne
    // `!hasTasks` hier bekaeme das BE weiterhin einen 400er.
    if (form.invalid || !this.hasIndustries || !this.hasTasks || !this.hasTaskSkills) {
      form.control.markAllAsTouched();
      this.runAllTaskValidations();
      return;
    }
    this.projekt = Projekt.clone(this.projectLocal);
    this.captureOriginalSnapshot();
    this.refreshFindings();
    this.changedProject.emit(this.projekt);
    this.editMode = false;

  }

  /** NP20-1057: sichert den Stand ohne jede Prüfung — Pflichtfelder greifen erst bei der Freigabe. */
  saveDraft() {
    if (!this.canSaveDraft || this.isSaving) return;
    this.projekt = Projekt.clone(this.projectLocal);
    this.captureOriginalSnapshot();
    this.refreshFindings();
    this.sentNewTasks = (this.projectLocal.tasks ?? []).filter(t => !t.uuid);
    if (this.isNewProject) this.editMode = false;
    this.draftProject.emit(this.projekt);
  }

  /** NP20-1057: ein nie freigegebener Entwurf hat nichts, was exportiert werden könnte. */
  get exportable(): boolean {
    return isExportable(this.projekt);
  }

  /** Verwerfen gibt es nur über einer Freigabe; einen nie freigegebenen Entwurf löscht der Papierkorb. */
  get canDiscardDraft(): boolean {
    return this.canSaveDraft && this.isDraft && this.exportable && !this.exportMode;
  }

  /** Dritte dürfen ein Projekt, über dem der Berater einen Zwischenstand hat, bis zur Freigabe nicht ändern. */
  get lockedByOwnerDraft(): boolean {
    return !this.canSaveDraft && !!this.projekt?.hasDraft;
  }

  get isDraft(): boolean {
    return isDraft(this.projekt);
  }

  /** Ein Entwurf ist auch ohne neue Änderung freigebbar, sonst bliebe er ohne Edit stecken. */
  get releaseBlocked(): boolean {
    return !this.hasIndustries || !this.hasTasks || !this.hasTaskSkills || this.isSaving
      || (!this.hasChanges() && !this.isDraft);
  }

  get draftSavedAt(): Date | null {
    return this.projekt?.draftSavedAt ? new Date(this.projekt.draftSavedAt) : null;
  }

  saveTask(idx: number): void {
    const task = this.projectLocal?.tasks?.[idx];
    if (!task || this.taskSaveBlocked(idx)) return;
    const sent = new Aufgabe(task.caption, task.details, JSON.parse(JSON.stringify(task.skills ?? [])), task.uuid);
    this.pendingTask = task;
    this.pendingSent = sent;
    this.taskSave.emit(new Aufgabe(sent.caption, sent.details, JSON.parse(JSON.stringify(sent.skills)), sent.uuid));
  }

  taskSaveBlocked(idx: number): boolean {
    return this.isSaving || this.pendingTask !== null || this.isNewProject || !this.projekt?.uuid
      || this.lockedByOwnerDraft || !this.unsavedTasks[idx]
      || (!this.canSaveDraft && this.missingTaskParts(idx).length > 0);
  }

  taskSaveHover(idx: number): string {
    if (this.isNewProject || !this.projekt?.uuid) return this.translateService.instant('project.taskSave.neverSaved');
    if (this.lockedByOwnerDraft) return this.translateService.instant('project.draft.lockedHint');
    const missing = this.canSaveDraft ? [] : this.missingTaskParts(idx);
    return missing.length ? missing.join('\n') : this.translateService.instant('project.taskSave.hover');
  }

  private missingTaskParts(idx: number): string[] {
    const task = this.projectLocal?.tasks?.[idx];
    if (!task) return [];
    const parts = this.getBlockingMessages(new Aufgabe(task.caption, task.details, task.skills).validate())
      .map(msg => this.translateService.instant(msg.key, msg.params) as string);
    if (!task.skills?.length) parts.push(this.translateService.instant('project.error.taskSkillsRequired'));
    return [...new Set(parts)];
  }

  deleteTask(idx: number) {
    this.projectLocal.tasks.splice(idx, 1);
    this.validationResults.splice(idx, 1);
  }

  deleteProjekt() {
    this.deletedProject.emit();
  }

  reloadSkills() {
    this.loadSkills();
  }

  private updateSelectedIndustryNames() {
    this.selectedIndustryNames = (this.projectLocal?.branchNames ?? [])
      .map(b => this.cachedIndustries.find(i => i.id === b.id)?.name ?? b.name)
      .filter((n): n is string => !!n);
  }

  private initProjektData() {
    this.validationResults = [];

    this.updateSelectedIndustryNames();

    for (const task of this.projectLocal?.tasks ?? []) {
      this.validationResults.push(
        new Aufgabe(task.caption, task.details, task.skills).validate()
      );
    }
  }

  private loadSkills() {
    this.skillService.query()
      .subscribe({
        next: (skills) => {
          this.skillList = skills.content;
          this.cdr.markForCheck();
        },
        error: (err) => console.error('Error loading skills:', err)
      });

    this.skillService.query('Vorgehensmodelle')
      .subscribe({
        next: (skills) => {
          this.methodSource = [...new Set(skills.content.map((m) => m.faehigkeit))];
          this.sortMethods();
          this.cdr.markForCheck();
        },
        error: (err) => console.error('Error loading methods:', err)
      });
  }

  onToggleEdit(): void {
    this.projectLocal = JSON.parse(JSON.stringify(this.projekt));
    this.captureOriginalSnapshot();
    this.initProjektData();
    this.editMode = true;
  }

  onToggleEditTask(idx: number): void {
    this.projectLocal = JSON.parse(JSON.stringify(this.projekt));
    this.captureOriginalSnapshot();
    this.editMode = true;
    setTimeout(() => {
      const el = this.textElementTask.toArray()[idx].nativeElement;
      const rem = parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );
      const offset = rem * 10;
      const y = el.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }, 0);
  }

  onCancelEdit(): void {
    this.projectLocal = JSON.parse(JSON.stringify(this.projekt));
    this.captureOriginalSnapshot();
    this.editMode = false;
    this.cancelEdit.emit();
  }

  ngDoCheck(): void {
    // Einmal pro CD-Zyklus statt bei jeder Template-Bindung berechnen; außerhalb des Edit-Modus inaktiv.
    this.hasUnsavedChanges =
      this.editMode &&
      !!this.projectLocal &&
      this.originalProjektJson !== JSON.stringify(this.projectLocal);
    this.refreshUnsavedMarks();
  }

  private refreshUnsavedMarks(): void {
    if (!this.editMode || !this.projectLocal || !this.projekt) {
      this.unsavedTasks = [];
      this.unsavedHead = false;
      return;
    }
    const savedTasks = this.projekt.tasks ?? [];
    this.unsavedTasks = (this.projectLocal.tasks ?? []).map(task => {
      const saved = task.uuid ? savedTasks.find(t => t.uuid === task.uuid) : undefined;
      return !saved || taskJson(saved) !== taskJson(task);
    });
    this.unsavedHead = headJson(this.projectLocal) !== headJson(this.projekt);
  }

  hasChanges(): boolean {
    return this.hasUnsavedChanges;
  }

  private captureOriginalSnapshot(): void {
    this.originalProjektJson = JSON.stringify(this.projekt);
  }

  get hasIndustries(): boolean {
    return (this.projectLocal?.branchNames?.length ?? 0) > 0;
  }

  /**
   * NP20-892: BE erzwingt `@NotEmpty tasks`; ohne mindestens eine Aufgabe scheitert
   * Save mit 400 Bad Request. Speichern-Button wird darum blockiert und der Nutzer
   * bekommt den Grund direkt im Tooltip zu sehen.
   */
  get hasTasks(): boolean {
    return (this.projectLocal?.tasks?.length ?? 0) > 0;
  }

  get hasTaskSkills(): boolean {
    return (this.projectLocal?.tasks ?? []).every(task => (task.skills?.length ?? 0) > 0);
  }

  get primarySaveHover(): string {
    return this.saveBlockedTooltip
      || this.translateService.instant(this.canSaveDraft ? 'project.draft.releaseHover' : 'project.saveHover');
  }

  onPrimarySave(form: NgForm): void {
    if (form.invalid || this.releaseBlocked) return;
    this.save(form);
  }

  get saveBlockedTooltip(): string {
    if (!this.editMode) return '';
    if (!this.hasIndustries) return this.translateService.instant('project.error.industriesRequired');
    if (!this.hasTasks) return this.translateService.instant('project.error.tasksRequired');
    if (!this.hasTaskSkills) return this.translateService.instant('project.error.taskSkillsRequired');
    return '';
  }

  checkExpanded(idx: number) {
    return this.activeTask === idx;
  }

  toggleActiveTask(idx: number) {
    this.activeTask = idx;
  }

  openDeleteModal(idx: number) {
    this.currentIdx = idx;
    this.modalService.open('deleteProject');
  }

  onProjectIndustriesChanged(updated: string[]) {
    this.availableIndustries$.subscribe(industries => {
      this.projectLocal.branchNames =
        industries.filter(i => updated.includes(i.name));
      this.updateSelectedIndustryNames();
      this.cdr.markForCheck();
    });
  }

  getAllIndustries() {
    this.availableIndustries$ = this.requestsService
      .getRequests(0, 300, RequestType.INDUSTRY, true)
      .pipe(
        map((list) =>
          (list.content ?? []).sort((a, b) =>
            a.name.localeCompare(b.name, 'de', {sensitivity: 'base'})
          )
        ),
        shareReplay(1)
      );
  }
}

function taskJson(task: Aufgabe): string {
  return JSON.stringify([task.caption, task.details, task.skills ?? [], task.uuid]);
}

function headJson(project: IProjekt): string {
  return JSON.stringify({...Projekt.clone(project), tasks: undefined});
}
